import React, { useMemo } from 'react';
import {
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowRightLeftIcon,
  Square3Stack3dIcon
} from '@/components/Icons';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/utils/routes';
import cn from '@/utils/cn';
import { Feature } from '@/config/featureFlags.definition';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import useAuth from '@/hooks/useAuth';
import { canAccessBatchPayments } from '@/utils/canAccessBatchPayments';

const allItems = [
  {
    label: 'Pay',
    feature: Feature.PAYMENT,
    icon: <ArrowUpIcon size="xs" variant="inherit" />,
    route: routes.payment,
    // Available to every profile that can already see the Pay flow
    requiresBusinessOwnerOrAdmin: false
  },
  {
    label: 'Transfer',
    feature: null,
    icon: <ArrowRightLeftIcon size="xs" variant="inherit" />,
    route: routes.transferMoney,
    requiresBusinessOwnerOrAdmin: false
  },
  {
    label: 'Batch Payment',
    feature: Feature.BATCH_PAYMENT,
    icon: <Square3Stack3dIcon size="xs" variant="inherit" />,
    route: routes.newBatchPayment,
    // Batch payments are a business-only capability: Business/Sole Trader/Organisation
    // Owner or Admin profiles only — never Personal profiles
    requiresBusinessOwnerOrAdmin: true
  }
];

interface MoveMoneyMenuProps {
  showNavBarMenu?: string;
  onOpenNavigation: (menu: string) => void;
  onCloseMenu: () => void;
}

export const MoveMoneyMenu: React.FC<MoveMoneyMenuProps> = ({
  showNavBarMenu,
  onOpenNavigation: handleNavBarMenuDropdown,
  onCloseMenu
}) => {
  const navigate = useNavigate();
  const { accountType, business } = useAuth() ?? {};

  const isBusinessOwnerOrAdmin = canAccessBatchPayments(accountType, business?.accessLevel);

  const moveMoneyItems = useMemo(
    () =>
      allItems.filter((item) => {
        if (item.feature && !isFeatureEnabled(item.feature)) return false;
        if (item.requiresBusinessOwnerOrAdmin && !isBusinessOwnerOrAdmin) return false;
        return true;
      }),
    [isBusinessOwnerOrAdmin]
  );

  const handleNavigate = (route: string) => {
    onCloseMenu();
    navigate(route);
  };

  return (
    <div className="relative">
      <button
        onClick={() => handleNavBarMenuDropdown('movemoney')}
        className="inline-flex items-center justify-center gap-2 rounded-[30px] bg-emerge-teal py-2 pl-3 pr-[10px] overflow-hidden text-xs font-medium text-emerge-navy">
        Move money
        <ChevronDownIcon
          size="xs"
          theme="dark"
          className={showNavBarMenu === 'movemoney' ? 'rotate-180' : ''}
        />
      </button>

      {showNavBarMenu === 'movemoney' ? (
        <div
          className={cn(
            'absolute top-10 right-0 bg-white text-emerge-navy z-50 cursor-pointer overflow-hidden w-[224px] max-h-[240px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.08)] rounded-[6px]'
          )}
          onMouseLeave={onCloseMenu}>
          {moveMoneyItems.map((item) => (
            <div
              key={item.route}
              className="flex items-center gap-2 px-4 py-3 bg-transparent hover:bg-emerge-base-2 cursor-pointer"
              onClick={() => handleNavigate(item.route)}>
              {item.icon}
              <span className="text-emerge-navy text-xs font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
